import { useState, useEffect, useMemo, useContext, useRef } from "react"
import useSettings from "../../hooks/Settings"
import QuestionaireField from '../../components/open/QuestionaireField'
import AIInterviewer from '../../components/open/AIinterviewer/AIInterviewer'
import INTERVIEWER_TEXTS from '../../components/open/AIinterviewer/interviewerTexts'
import { v4 as uuid } from 'uuid';
import firebase from '../../firebase/compat';
import { Responses } from "../../StateManagment/Responses";
import { useFirestoreGeneralOrderBy, useFirestoreGeneral, useFirestoreID } from "../../firebase/useFirestore";
import Location from "../../hooks/Location";
import { client } from "../../hooks/Client";
import ImmersiveProgressBar from "../../components/open/elements/ImmersiveProgressBar";
import { QuestionnaireContext } from "../../StateManagment/Questionnaire";
import { useTranslation } from '../../StateManagment/TranslationContext';
import notDeleted from "../../helpers/notDeleted";
import filterFieldsForMoment from "../../helpers/questionnaire/filterFieldsForMoment";
import filterFieldsForResponses from "../../helpers/questionnaire/filterFieldsForResponses";
import buildNavItems from "../../helpers/questionnaire/buildNavItems";
import questionnaireProgress from "../../helpers/questionnaire/questionnaireProgress";
import hiddenQuestionIDs from "../../helpers/questionnaire/hiddenQuestionIDs";
import isRedesignMode from "../../helpers/questionnaire/isRedesignMode";
import kioskReturnLink from "../../helpers/questionnaire/kioskReturnLink";
import { readStoredTextScale, storeTextScale } from '../../helpers/questionnaire/textScale';
import browserStorage from '../../utils/browserStorage';
import normalizePersonaParam from "../../helpers/questionnaires/normalizePersonaParam";
import buildTranslationStrings from "../../helpers/questionnaire/buildTranslationStrings";
import isFlagOn from '../../helpers/isFlagOn';
import { applyLinkSettings, miniGameBlocked } from '../../helpers/questionnaires/linkSettings';
import { resolveQuestionnaireSession, markQuestionnaireSessionDone, sessionLinkKey, currentNavigationType } from "../../helpers/questionnaire/questionnaireSession";
import LanguageSwitcher from "../../components/open/LanguageSwitcher";
import TextSizeSwitcher from "../../components/open/TextSizeSwitcher";
import AddressStep from "../../components/open/AddressStep";
import NameStep from "../../components/open/NameStep";
import { ADDRESS_STEP_TEXTS, NAME_STEP_TEXTS } from "../../components/open/linkStepTexts";
import scaleStartLabel from "../../helpers/questionnaire/scaleStartLabel";

const QuestionnaireOpen = () => {
  // Context
  const [responses, setResponses] = useContext(Responses);
  const { fieldIndex, setFieldIndex, isTransitioning, settings, setSettings, gamificationEnabled, setGamificationEnabled } = useContext(QuestionnaireContext);
  const { translations, setTranslationInput } = useTranslation();

  // State
  const [saved, setSaved] = useState(false);
  const [stringsToTranslate, setStringsToTranslate] = useState({});

  // Get url params
  const questionnaireId = Location()[3]
  const moment = Location()[4];
  const research = Location()[5];
  // Deellink zet 'undefined' in dit segment bij een anonieme meting —
  // normaliseren, anders wordt die tekst als deelnemer-ID opgeslagen (inbox #93)
  // AI-CONTEXT: See docs/pitfalls.md#database-url-sentinel-string-persisted
  const persona = normalizePersonaParam(Location()[6]);
  const test = Location()[7];
  const languageCode = Location()[8]

  // Firestore
  const questionaires = useFirestoreID('Questionnaires', questionnaireId ? questionnaireId : '0')
  const researches = useFirestoreGeneral('Research', 'ID', research ? research : '');
  const measureMoments = useFirestoreGeneral('MeasureMoments', 'ID', moment ? moment : '');
  const allFields = useFirestoreGeneralOrderBy(
    'QuestionnaireFields',
    'QuestionnaireID',
    questionnaireId,
    'Position',
    'asc'
  );

  // Een meetmoment kan vragen uitzetten. Lukt die lees niet, dan is de lijst
  // leeg en krijgt de respondent alles te zien — bewust fail-open: dit is een
  // zichtbaarheidsinstelling, geen beveiligingspoort.
  const excludedFieldIDs = measureMoments?.[0]?.ExcludedFieldIDs;

  // De hook geeft '' zolang de snapshot onderweg is en [] als het meetmoment
  // niet bestaat. Wachten tot dat verschil bekend is, anders staan de uitgezette
  // vragen eerst kort in beeld en draait de vertaalronde er twee keer overheen.
  const momentPending = Boolean(moment) && measureMoments === '';

  const fields = useMemo(
    () => (momentPending
      ? []
      : filterFieldsForMoment((allFields || []).filter(notDeleted), excludedFieldIDs)),
    [allFields, excludedFieldIDs, momentPending]
  );

  // Build stable signatures so the effect doesn't re-run on mere identity changes
  const qSig = useMemo(() =>
    (questionaires || []).map(q => `${q.ID}|${q.Title}|${q.Description}`).join('¦'),
    [questionaires]
  );

  const fSig = useMemo(() =>
    (fields || []).map(f =>
      `${f.ID}|${f.Type}|${f.Question}|${f.Explainer}|${scaleStartLabel(f)}|${f.ReachEndLabel}`
    ).join('¦'),
    [fields]
  );

  // Remember last payload we sent, to avoid re-setting identical state
  const lastSentRef = useRef({ lang: null, keysHash: null });

  useEffect(() => {
    let cancelled = false;

    const buildAndSendTranslations = async () => {
      if (!questionaires || !fields || !languageCode) return;

      // Fast path for Dutch
      if (languageCode === 'nl') {
        const nlHash = 'nl-empty';
        if (
          lastSentRef.current.lang === 'nl' &&
          lastSentRef.current.keysHash === nlHash
        ) return;

        if (cancelled) return;
        setStringsToTranslate({});
        setTranslationInput({ strings: {}, language: languageCode });
        lastSentRef.current = { lang: 'nl', keysHash: nlHash };
        return;
      }

      // 1) base UI strings
      const base = {
        vragenlijst: "Vragenlijst",
        "Einde van de vragenlijst": "Dit is het einde van de vragenlijst",
        "Previewversie": "Dit is een previewversie van de vragenlijst. De antwoorden kunnen niet worden opgeslagen.",
        "Klik om op te slaan": "Klik op verzenden om de vragenlijst op te slaan",
        "Geef hier uw antwoord": "Geef hier uw antwoord",
        "Volgende": "Volgende",
        "Bezig": "Bezig…",
        "Vraag": "Vraag",
        "Beantwoord alle rijen": "Beantwoord alle rijen om verder te gaan",
        "Vraag verplicht": "Deze vraag is verplicht. Geef een antwoord om verder te gaan.",
        "Verplichte vraag": "Verplichte vraag",
        "Controleren mislukt": "Het controleren van de vraag is niet gelukt. Probeer het opnieuw.",
        "Anders, namelijk...": "Anders, namelijk...",
        "Terug": "Terug",
        "Verzenden": "Verzenden",
        "Antwoorden opgeslagen": "Uw antwoorden zijn opgeslagen",
        "Opnieuw": "Opnieuw",
        "Onderzoek niet beschikbaar": "Deze vragenlijst kan nu niet worden geopend. Probeer het later opnieuw.",
        "Kopie resultaten": "Kopie van resultaten downloaden",
        "Downloaden": "Downloaden",
        "Je kunt deze pagina nu verlaten": "Je kunt deze pagina nu verlaten",
        // Sleutel zonder dubbele punt: zo vraagt het eindscherm hem op
        // (questionnaireTranslationKeys.guard.test.js bewaakt dat).
        "Bedankt voor het invullen": "Bedankt voor het invullen. Je antwoorden zijn opgeslagen. Je mag het scherm nu sluiten.",
        "Schrijf hier je antwoord": "Schrijf hier je antwoord",
        // Ankers van de schuifbalk bij meetstandaard-matrixvragen (vasScale.js)
        "Helemaal niet": "Helemaal niet",
        "Helemaal wel": "Helemaal wel",
        // Wat een schermlezer hoort bij een onaangeraakte schuifbalk (VasSlider)
        "Nog niet beantwoord": "Nog niet beantwoord",
        // De AI-interviewer: tekst is sleutel, zie interviewerTexts.js
        ...Object.fromEntries(INTERVIEWER_TEXTS.map((text) => [text, text])),
        // De adres- en naamstap: dezelfde lijst die de stap zelf als terugval
        // gebruikt, zodat er geen tekst onvertaald achterblijft.
        ...ADDRESS_STEP_TEXTS,
        ...NAME_STEP_TEXTS,
      };

      // 2) de teksten van de vragenlijst zelf — zelfde set als op het printvel
      const strings = await buildTranslationStrings({
        questionnaires: questionaires,
        fields,
        client,
        base,
      });

      if (cancelled) return;

      // Compute a compact hash of what we're sending to avoid re-setting same data
      const keysHash = `${languageCode}|${Object.keys(strings).length}`;

      if (
        lastSentRef.current.lang === languageCode &&
        lastSentRef.current.keysHash === keysHash
      ) {
        return; // no change → no state update → no loop
      }

      setStringsToTranslate(strings);
      setTranslationInput({ strings, language: languageCode });
      lastSentRef.current = { lang: languageCode, keysHash };
    };

    buildAndSendTranslations();
    return () => { cancelled = true; };
  }, [qSig, fSig, languageCode, setTranslationInput]);


  // Session handling — één invulling = één bezoek aan de link (inbox #171).
  // Een nieuwe scan of link start een nieuwe sessie; herladen houdt de lopende
  // vast tot de vragenlijst af is. Eén keer per mount bepaald.
  // AI-CONTEXT: See src/helpers/questionnaire/questionnaireSession.js
  const sessionLink = sessionLinkKey({ questionnaireId, moment, research, persona });
  const sessionIdRef = useRef(null);
  if (sessionIdRef.current === null) {
    sessionIdRef.current = resolveQuestionnaireSession({
      storage: sessionStorage,
      linkKey: sessionLink,
      navigationType: currentNavigationType(),
      newId: uuid,
    });
  }
  const sessionId = sessionIdRef.current;

  // --- Adresstap (Research.AskAddressFirst) of naamstap (Research.AskNameFirst) ---
  // De vragen verschijnen pas als matchQuestionnaireAddress / matchQuestionnaireName
  // de stap van deze sessie heeft vastgelegd; saveQuestionnaireResponse zet
  // daarna de respondent op de antwoorden. Zolang het onderzoek laadt (de hook
  // geeft dan '') weten we niet of een stap nodig is: dan niets tonen, niet de
  // vragen. Adres gaat vóór naam; de instellingen laten ze niet samen aan.
  // AI-CONTEXT: See docs/features/adres-koppeling-algemene-qr.md
  // AI-CONTEXT: See docs/features/deelnemer-koppeling-algemene-link.md
  // AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
  const researchPending = Boolean(research) && researches === '';
  // Gelezen maar leeg: het onderzoek bestaat niet (meer) of de lezing werd
  // geweigerd. Dan weten we niet of er een adres- of naamstap hoort, en vallen
  // we dicht: geen vragen. Niet in de proefversie, die geeft het id van de
  // vragenlijst mee als er geen onderzoek is. Gemeten 2026-10-14: in 60 dagen
  // kwam dit buiten de proefversie niet voor (/complete, code review 2).
  // AI-CONTEXT: See docs/pitfalls.md#security-public-page-reads-its-own-gates
  const researchMissing = test !== 'test' && Boolean(research) && Array.isArray(researches) && researches.length === 0;
  const linkStep = test === 'test'
    ? ''
    : isFlagOn(researches?.[0]?.AskAddressFirst)
      ? 'address'
      : isFlagOn(researches?.[0]?.AskNameFirst) ? 'name' : '';
  const linkStepKey = `${linkStep}StepDone:${sessionId}`;
  const [linkStepsDone, setLinkStepsDone] = useState(/** @type {Record<string, boolean>} */ ({}));
  const linkStepDone = Boolean(linkStepsDone[linkStepKey]) || sessionStorage.getItem(linkStepKey) === 'true';
  const showLinkStep = Boolean(linkStep) && !linkStepDone;

  const linkStepDoneHandler = () => {
    sessionStorage.setItem(linkStepKey, 'true');
    setLinkStepsDone((prev) => ({ ...prev, [linkStepKey]: true }));
  };

  // Create session doc (exactly once), with clear logging
  const creatingRef = useRef(false);

  useEffect(() => {
    // Per sessie, niet per tabblad: een tweede invuller in hetzelfde tabblad
    // krijgt een eigen sessie en dus een eigen sessiedocument (inbox #171).
    const alreadyCreated = sessionStorage.getItem(`sessionDocCreated:${sessionId}`) === '1';
    if (alreadyCreated) {
      console.log('[Sessions] Session doc already created for', sessionId);
      return;
    }

    if (creatingRef.current) return;

    const userAgent = navigator.userAgent || '';
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Categorize device type
    let deviceType = 'desktop';
    if (width <= 767) deviceType = 'mobile';
    else if (width <= 1024) deviceType = 'tablet';
    else deviceType = 'laptop/desktop';

    // Browser language (e.g., "nl-NL", "en-US")
    const browserLanguage = navigator.language || (navigator.languages && navigator.languages[0]) || '';

    const run = async () => {
      try {
        creatingRef.current = true;

        // Via een functie: de Sessions-regel laat anoniem aanmaken niet meer
        // toe. De server toetst het onderzoek en bepaalt tijdstip en volgnummer.
        console.log('[Sessions] Creating session doc…', {
          sessionId, research, moment, questionnaireId, persona, languageCode
        });

        await firebase.app().functions('europe-west1').httpsCallable('saveQuestionnaireSession')({
          sessionId,
          client,
          researchId: research || '',
          momentId: moment || '',
          questionnaireId: questionnaireId || '',
          personaId: persona || '',
          userAgent,
          screenWidth: width,
          screenHeight: height,
          deviceType,
          browserLanguage,
          languageCode: languageCode || '',
          settings: settings || {},
        });

        console.log('[Sessions] Session doc created/updated ✓');
        console.table({
          SessionId: sessionId,
          DeviceType: deviceType,
          Screen: `${width}x${height}`,
          LanguageCode: languageCode || '',
          ResearchID: research || '',
        });

        sessionStorage.setItem(`sessionDocCreated:${sessionId}`, '1');
      } catch (err) {
        console.error('[Sessions] Failed to create session doc:', err);
      } finally {
        creatingRef.current = false;
      }
    };

    void run();

    // Opnieuw zolang het nog niet gelukt is (bv. onderzoek nog niet geladen); de
    // functie maakt een sessie maar één keer aan.
  }, [
    sessionId,
    research,
    moment,
    questionnaireId,
    persona,
    languageCode,
    settings,
  ]);

  // ------- derive the first research once -------
  // Zoals deze deellink het onderzoek ziet: de link kan instellingen van het
  // onderzoek uitzetten (`?uit=…`, gekozen op 'Vragenlijst delen'), nooit aanzetten.
  const linkSearch = window.location.search;
  const s = useMemo(() => applyLinkSettings(researches?.[0], linkSearch), [researches, linkSearch]);

  // Build the next settings value from *primitive* deps so the effect only runs when they change
  // Via `isFlagOn`, niet met `=== 'true'`: deze vlaggen staan in meer vormen in
  // de database dan één vergelijking aankan. `SendCopyOption` bijvoorbeeld start
  // bij elk nieuw onderzoek op 'off' en staat bij 14 onderzoeken op 'on' — die
  // werden hier als UIT gelezen, dus die respondenten kregen nooit de optie om
  // een kopie van hun antwoorden te ontvangen.
  // AI-CONTEXT: See docs/pitfalls.md#ui-togglebutton-string-only-branches
  // Buiten de memo uitgerekend, zodat ook deze een primitieve dependency is.
  const miniGameOff = miniGameBlocked(s);
  const nextSettings = useMemo(() => ({
    sendCopyOption: isFlagOn(s?.SendCopyOption),
    readAloud:      isFlagOn(s?.ReadAloud),
    // De mini animatie is onderdeel van de geanimeerde voortgangsbalk: één
    // schakelaar, en het oude veld `MiniGame` telt niet meer.
    // De balk mag naast de AI-interviewer, de mini animatie niet.
    progressBar:    isFlagOn(s?.ProgressBar),
    miniGame:       isFlagOn(s?.ProgressBar) && !miniGameOff,
    aiInterviewer:  isFlagOn(s?.AIInterviewer),
    // Adresstap: elke opslag draagt dan de vlag, en de server zet het
    // huishouden op de antwoorden. AI-CONTEXT: See docs/features/adres-koppeling-algemene-qr.md
    addressStep:    isFlagOn(s?.AskAddressFirst),
    // Naamstap: idem, met de deelnemer zodra een medewerker de invulling
    // gekoppeld heeft. AI-CONTEXT: See docs/features/deelnemer-koppeling-algemene-link.md
    nameStep:       isFlagOn(s?.AskNameFirst),
    // Schuifbalk in plaats van rondjes bij schaal- en matrixvragen.
    // AI-CONTEXT: See docs/features/vas-slider.md
    vas:            isFlagOn(s?.VisualAnalogScale),
  }), [
    s?.SendCopyOption, s?.ReadAloud,
    s?.ProgressBar, s?.AIInterviewer, s?.AskAddressFirst, s?.AskNameFirst, s?.VisualAnalogScale, miniGameOff
  ]);

  useEffect(() => {
    // Only update settings if there is a real change
    setSettings(prev => {
      if (!prev) return nextSettings;
      const changed = Object.keys(nextSettings).some(k => prev[k] !== nextSettings[k]);
      return changed ? nextSettings : prev;
    });
  }, [nextSettings, setSettings]);

  useEffect(() => {
    setGamificationEnabled(!!nextSettings.miniGame);
  }, [nextSettings.miniGame, setGamificationEnabled]);

  // SendCopyOption 
  const sendCopyOption = researches && researches.map(research => research.SendCopyOption);

  // Conditionele vragen: wat de respondent op DIT moment moet zien, gegeven zijn
  // antwoorden tot nu toe. Zonder regels komt dezelfde array terug, dus een
  // vragenlijst zonder toon-als-regels rekent hier niets extra's uit.
  // De vertaalronde hierboven blijft op `fields` draaien — een vraag die straks
  // pas verschijnt moet al vertaald zijn.
  // AI-CONTEXT: See docs/features/conditional-questions.md
  const visibleFields = useMemo(
    () => filterFieldsForResponses(fields, responses),
    [fields, responses]
  );

  // Heeft deze vragenlijst toon-als-regels? Zo niet, dan blijft alles hieronder
  // zich exact gedragen als voorheen — geen klemmen, teller en stippen gewoon
  // in beeld, geen opruimwerk.
  const hasVisibilityRules = useMemo(
    () => (fields || []).some(field => field?.ShowWhen?.FieldID),
    [fields]
  );

  // Vragen die door een dichtgeklapte tak verborgen raakten. Hun al opgeslagen
  // antwoorden zijn wezen: ze tellen mee in elke grafiek, export en AI-context.
  // Het opruimen lift mee op de eerstvolgende opslag-aanroep.
  // AI-CONTEXT: See docs/features/conditional-questions.md
  const hiddenFieldIDs = useMemo(
    () => (hasVisibilityRules ? hiddenQuestionIDs(fields, visibleFields) : []),
    [hasVisibilityRules, fields, visibleFields]
  );

  // Top-level items (section markers + top-level questions), in Position order.
  const topLevelFields = useMemo(
    () => (visibleFields || []).filter(field => field.SectionID === ''),
    [visibleFields]
  );

  // Navigation steps. Gamification ON → flatten each section's questions into
  // individual steps so they advance one-by-one (the mini-game animations can't run
  // with a whole section on one page). OFF → sections stay grouped on one page.
  const navItems = useMemo(
    () => buildNavItems(topLevelFields, visibleFields, gamificationEnabled),
    [topLevelFields, visibleFields, gamificationEnabled]
  );

  // Current step → surface its section title + colour in the sky.
  const currentItem = navItems ? navItems[fieldIndex] : null;
  const currentSection = currentItem
    ? (currentItem.Type === 'section'
        ? { title: currentItem.Question, color: currentItem.Color }
        : (currentItem._section || null))
    : null;
  const sectionTitle = currentSection ? (translations?.[currentSection.title] || currentSection.title) : '';
  const sectionColor = currentSection?.color || '';

  // Helper: get response title (translated)
  const getResponseTitle = (id) => {
    const matchingField = fields && fields.find(field => field.ID === id);
    return matchingField ? (translations?.[matchingField.Question] || matchingField.Question) : null;
  };

  // Build downloadable responses string
  const createResponsesString = () => {
    const array = responses?.map(response => ({
      Vraag: getResponseTitle(response.FieldID),
      Antwoord: response.Input,
    })) || [];
    return array.map(item => `Vraag: ${item.Vraag}\nAntwoord: ${item.Antwoord}`).join("\n\n");
  };

  // Download results handler
  const downloadResultsHandler = (e) => {
    e.target.style.color = "lightgray";
    e.target.innerHTML = 'Downloaden...';

    const filename = `responses-${questionnaireId}-${new Date().toISOString()}.txt`;
    const blob = new Blob([createResponsesString()], { type: 'text/plain' });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    e.target.innerHTML = 'Gedownload';
  };

  const endReached = fieldIndex + 1 > (navItems ? navItems.length : 0);

  // Af: de volgende keer (ook 'Opnieuw', dat herlaadt) begint een nieuwe sessie.
  // Alleen met stappen in beeld — zolang de vragen laden is de lijst leeg en
  // zou 'einde bereikt' al waar zijn.
  const questionnaireDone = endReached && Boolean(navItems && navItems.length > 0);
  useEffect(() => {
    if (questionnaireDone) markQuestionnaireSessionDone({ storage: sessionStorage, linkKey: sessionLink });
  }, [questionnaireDone, sessionLink]);

  // Met regels ligt het aantal stappen niet vast: een antwoord kan er vragen bij
  // toveren en dan zou de balk terugspringen. De vorige uitkomst dient als
  // ondergrens. Toewijzen tijdens de render is veilig — Math.max is idempotent.
  // AI-CONTEXT: See docs/features/conditional-questions.md
  const progressRef = useRef(null);
  const progress = questionnaireProgress({
    stepIndex: fieldIndex,
    visibleSteps: navItems ? navItems.length : 0,
    previous: progressRef.current,
    monotone: hasVisibilityRules,
  });
  progressRef.current = progress;

  // Een exact totaal dat niet klopt is slechter dan geen totaal.
  const stepLabel = hasVisibilityRules
    ? `${translations?.["Vraag"] || "Vraag"} ${fieldIndex + 1}`
    : `${fieldIndex + 1} / ${navItems ? navItems.length : 0}`;
  // In de lucht van de geanimeerde balk staat het woord erbij: daar staat de
  // teller los, zonder kop eromheen (testronde 2026-10-15, B7).
  const skyStepLabel = hasVisibilityRules
    ? stepLabel
    : `${translations?.["Vraag"] || "Vraag"} ${stepLabel}`;

  // Cosmetic redesign gate — standard mode only; gamified (ProgressBar, met de mini animatie)
  // and AI interviewer always keep their existing look. In de voorbeeldweergave
  // vanuit de bouwer telt een ontbrekend onderzoek als 'niet gamified' in plaats
  // van als 'nog niet geladen' — anders krijgt elke vragenlijst zonder gekoppeld
  // onderzoek daar de oude look te zien (updateverzoek 2026-09-10).
  const redesign = isRedesignMode(s, test === 'test');

  useEffect(() => {
    if (endReached && fieldIndex > 1) {
      /* Only redirect if we're in 'kiosk' mode */
      const kioskLink = kioskReturnLink(window.location.pathname, window.location.search);
      if (kioskLink) {
        console.log('KIOSK MODE: redirecting to original kiosk onboarding');

        setTimeout(() => {
          window.location.href = kioskLink;
        }, 6_000);
      }
    }
  }, [endReached, fieldIndex]);

  // Tekstgrootte die de respondent zelf kiest. De keuze staat in de opslag van
  // zijn eigen browser: deze pagina kent geen inlog, dus er is geen profiel om
  // hem in te bewaren. Lukt lezen of schrijven niet (privémodus), dan valt hij
  // terug op normaal in plaats van de vragenlijst te slopen.
  const [textScale, setTextScale] = useState(() => readStoredTextScale(browserStorage()));

  const textScaleHandler = (next) => {
    setTextScale(next);
    storeTextScale(browserStorage(), next);
  };

  return (
    <div className='page-container'>
      {/* `qo-immersive` is de haak voor de spelweergave. Daar vult een
          landschap het hele scherm (`position: fixed`), en de kop stond er
          zonder eigen ondergrond bovenop -- donkergrijze letters op groene
          heuvels. De stylesheet kan die twee standen niet zelf uit elkaar
          houden: `progressBar` staat op het Research-document en raakt de DOM
          verder nergens. */}
      <div id='questionniare-container'
        className={`${isTransitioning ? 'transitioning' : ''}${redesign ? ' qo-redesign' : ''}${settings.progressBar ? ' qo-immersive' : ''}`}
        style={{ '--qo-tekstschaal': textScale }} >
        {/* Taal zit in de url (segment 8); de wissel herschrijft alleen dat segment,
            dus de pagina blijft staan en de ingevulde antwoorden gaan niet verloren. */}
        {/* Tekstgrootte en taal staan samen in één balk rechtsboven, zodat ze
            elkaar niet overlappen als de taalnaam breder wordt.

            De tekstgrootte alleen in de standaardweergave: de oude weergave
            (mini animatie, geanimeerde voortgangsbalk, AI-interviewer) krijgt
            zijn lettergroottes uit de app-brede typografie in layout.css, en
            die mag niet meeschalen omdat hij overal in de app geldt. De knop
            zou daar dus staan en niets doen — erger dan geen knop, want juist
            wie hem nodig heeft blijft dan klikken. Platformbreed gaat het om
            16 van de 360 onderzoeken. */}
        <div className='qo-open-controls'>
          {redesign && <TextSizeSwitcher scale={textScale} onChange={textScaleHandler} />}
          <LanguageSwitcher languageCode={languageCode} />
        </div>
        {redesign && (
          <div className="qo-topbar">
            <div className="qo-topbar-meta">
              {endReached || showLinkStep ? '' : stepLabel}
            </div>
            <div className="qo-topbar-track">
              <div
                className="qo-topbar-fill"
                style={{ width: `${endReached ? 100 : progress.percent}%` }}
              />
            </div>
          </div>
        )}
        {/* De adres- of naamstap is geen vraag: geen stippen en geen teller zolang hij in beeld is. */}
        {redesign && !endReached && !hasVisibilityRules && !showLinkStep && (
          <div className="qo-navdots">
            {(navItems || []).map((item, i) => (
              <i key={item.ID || i} className={i === fieldIndex ? 'on' : ''} />
            ))}
          </div>
        )}
        {/* Niets tonen zolang het meetmoment onderweg is: met een lege
            veldenlijst zou de vragenlijst even 'einde van de vragenlijst'
            melden, en met een ongefilterde lijst flitsen de uitgezette vragen
            in beeld. */}
        {researchMissing && (
          <p className="address-step-problem" role="alert">
            {translations?.["Onderzoek niet beschikbaar"] || "Deze vragenlijst kan nu niet worden geopend. Probeer het later opnieuw."}
          </p>
        )}
        {!momentPending && !researchPending && !researchMissing && questionaires && questionaires.map(questionaire => (
          <div key={questionaire.ID} id='questionnaire-field-outer-container'>
            {/* De geanimeerde voortgangsbalk vervangt de STAPTELLER, niet de
                introductie. Dit blok ging in zijn geheel op `display: none`
                zodra `progressBar` aanstond, en daarmee verdwenen ook de titel
                en de introtekst -- het eerste wat een respondent leest. Alleen
                de teller hoort te wijken, anders staat de stand twee keer op
                het scherm. → docs/inbox.md #245 */}
            {/* Geen inline stijl: die verslaat elke stylesheet-regel, en dan kan
                de compacte balk van de spelweergave zijn eigen ondergrond nooit
                krijgen. De doorzichtige achtergrond en de stapelvolgorde staan
                nu in questionnaire.css, met dezelfde reikwijdte als hiervoor. */}
            <div id='page-header-open-questionnaire'>
              <>
                <p>{translations.vragenlijst || 'Vragenlijst'}</p>
                <h1>{translations[questionaire.ID]?.title || questionaire.Title}</h1>
                <p>{translations[questionaire.ID]?.description || questionaire.Description}</p>
              </>
              {/* Show progress as '1/10' */}
              {endReached || settings.progressBar || showLinkStep ? null : <span>{stepLabel}</span>}
            </div>

            {settings.progressBar && (
              <ImmersiveProgressBar
                current={fieldIndex}
                total={progress.total}
                // De naam- of adresstap is geen sectie: anders staat de titel
                // van de eerste sectie al boven 'Je naam' (testronde 2026-10-13).
                sectionTitle={showLinkStep ? '' : sectionTitle}
                sectionColor={showLinkStep ? '' : sectionColor}
                stepLabel={endReached || showLinkStep ? '' : skyStepLabel}
              />
            )}

            {showLinkStep && linkStep === 'address' ? (
              <AddressStep
                client={client}
                research={research}
                moment={moment}
                sessionId={sessionId}
                translations={translations}
                onDone={linkStepDoneHandler}
              />
            ) : showLinkStep && linkStep === 'name' ? (
              <NameStep
                client={client}
                research={research}
                moment={moment}
                sessionId={sessionId}
                translations={translations}
                onDone={linkStepDoneHandler}
              />
            ) : endReached ? (
              <>
                <div id="questionnaire-end-container">
                  <div style={{ position: 'relative', zIndex: 10, display: saved ? 'none' : 'block' }}>
                    {redesign && (
                      <div className="qo-endcheck">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                      </div>
                    )}
                    <h2>{translations?.["Einde van de vragenlijst"] || "Dit is het einde van de vragenlijst"}</h2>
                    {test === 'test' ? (
                      <p>{translations?.["Previewversie"] || "Dit is een previewversie van de vragenlijst. De antwoorden kunnen niet worden opgeslagen."}</p>
                    ) : (
                      <p>{translations?.["Bedankt voor het invullen"] || "Bedankt voor het invullen. Je antwoorden zijn opgeslagen. Je mag het scherm nu sluiten."}</p>
                    )}
                    <div className="questionnaire-button-container">
                      {/* buttons intentionally omitted */}
                    </div>
                  </div>

                  <div id='responses-send-container' style={{ display: saved ? 'flex' : 'none' }}>
                    <h2>{translations?.["Antwoorden opgeslagen"] || "Uw antwoorden zijn opgeslagen"}</h2>
                    <button onClick={() => window.location.reload()}>{translations?.["Opnieuw"] || "Opnieuw"}</button>

                    {/* FIX: settings.sendCopyOption is boolean */}
                    <div id='send-copy-container' style={{ display: settings.sendCopyOption ? 'block' : 'none' }}>
                      <p><b>{translations?.["Kopie resultaten"] || "Kopie van resultaten downloaden"}</b></p>
                      <button className="button-simple" onClick={downloadResultsHandler}>
                        {translations?.["Downloaden"] || "Downloaden"}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              // Staat 'AI Interviewer' aan, dan voert Decca het gesprek in plaats
              // van dat de respondent zelf invult. De keten draait volledig in
              // Europa (Whisper + Qwen bij OVH, eigen stem); de OpenAI-versie is
              // op 2026-09-15 om privacyredenen verwijderd.
              // AI-CONTEXT: See docs/features/ai-interviewer-ovh-stt-tts.md
              settings.aiInterviewer ? (
                <AIInterviewer excludedFieldIDs={excludedFieldIDs} />
              ) : (
                <QuestionaireField
                  questionaire={questionaire}
                  topLevelFields={navItems}
                  allFields={visibleFields}
                  hiddenFieldIDs={hiddenFieldIDs}
                  setFieldIndex={setFieldIndex}
                  fieldIndex={fieldIndex}
                  compagnyId={client}
                  languageCode={languageCode}
                  moment={moment}
                  persona={persona}
                  questionnaireId={questionnaireId}
                  research={research}
                  sessionId={sessionId}
                />
              )
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default QuestionnaireOpen

